/**
 * VALUATION SLICE - Redux Toolkit
 * 
 * Gerenciamento de estado do valuation DCF
 * - ValuationSnapshot completo (Sétima + 555)
 * - Cenários (Pessimista, Base, Otimista)
 * - Premissas macro e por empresa
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  ValuationSnapshot, 
  CompanyAssumptions, 
  MacroAssumptions, 
  ScenarioDefinition,
  EquityBridgeInputs,
  DiscountInputs
} from '../../types/valuation';
import { 
  ValuationEngine, 
  SETIMA_CENARIO_BASE,
  SETIMA_CENARIO_PESSIMISTA,
  SETIMA_CENARIO_OTIMISTA,
  STUDIOS555_CENARIO_BASE,
  STUDIOS555_CENARIO_PESSIMISTA,
  STUDIOS555_CENARIO_OTIMISTA,
  DEFAULT_SETIMA_ASSUMPTIONS,
  DEFAULT_555_ASSUMPTIONS
} from '../../core/ValuationEngine.v2';
import { calculateKe } from '../../utils/capmCalculator';
import { 
  detectBaseYear,
  generateDynamicCapex,
  generateDynamicNWC,
  SETIMA_CAPEX_TEMPLATE,
  SETIMA_NWC_TEMPLATE,
  STUDIOS555_CAPEX_TEMPLATE,
  STUDIOS555_NWC_TEMPLATE
} from '../../utils/dynamicAssumptions';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Criar ScenarioDefinition completo a partir de cenário parcial
 */
function createScenarioDefinition(
  partial: Pick<CompanyAssumptions, 'crescimento' | 'margemEBITDA'> & {
    margemBruta?: CompanyAssumptions['margemEBITDA'];
    margemLiquida?: CompanyAssumptions['margemEBITDA'];
  },
  nome: string,
  descricao: string,
  premissa: string,
  probabilidade?: number
): ScenarioDefinition {
  // Valores padrão caso as margens não sejam fornecidas (cenários antigos)
  const defaultMargins = {
    2025: 0,
    2026: 0,
    2027: 0,
    2028: 0,
    2029: 0
  };

  return {
    nome,
    descricao,
    crescimento: partial.crescimento,
    margemBruta: partial.margemBruta || defaultMargins,
    margemEBITDA: partial.margemEBITDA,
    margemLiquida: partial.margemLiquida || defaultMargins,
    premissa,
    probabilidade
  };
}

/**
 * P0.5 FIX: Helper para criar CompanyAssumptions completo com TODOS os parâmetros
 * WACC FIX: Agora calcula WACC dinamicamente via CAPM (Ke) para empresas 100% equity
 * ✅ ANOS DINÂMICOS: Agora inclui baseYear para projeções dinâmicas
 * 
 * ⚠️ CRITICAL: Esta função é a ÚNICA forma de criar CompanyAssumptions.
 *              NUNCA use DEFAULT_*_ASSUMPTIONS diretamente no código.
 *              Sempre passe TODOS os parâmetros editáveis pelo usuário.
 * 
 * @param empresa - 'setima' ou 'studios555'
 * @param scenario - Cenário com crescimento e margemEBITDA
 * @param beta - Beta do companyParams state (M3)
 * @param capex - CAPEX do companyParams state (M2)
 * @param nwc - NWC do companyParams state (M2)
 * @param macro - MacroAssumptions para cálculo do CAPM (Ke)
 * @param baseYear - Último ano da DRE (para projeções dinâmicas)
 * @returns CompanyAssumptions completo com TODOS os parâmetros
 * 
 * @example
 * const assumptions = createCompanyAssumptions(
 *   'setima',
 *   scenarios.setima.base,
 *   companyParams.setima.beta,
 *   companyParams.setima.capex_pct_receita,
 *   companyParams.setima.nwc_pct_delta_receita,
 *   macroAssumptions,
 *   2024  // baseYear
 * );
 */
export function createCompanyAssumptions(
  empresa: 'setima' | 'studios555',
  scenario: ScenarioDefinition,
  beta: number,
  capex: CompanyAssumptions['capex_pct_receita'],
  nwc: CompanyAssumptions['nwc_pct_delta_receita'],
  macro: MacroAssumptions,
  baseYear: number = 2024  // ✅ ANOS DINÂMICOS: Novo parâmetro
): CompanyAssumptions {
  const defaults = empresa === 'setima' 
    ? DEFAULT_SETIMA_ASSUMPTIONS 
    : DEFAULT_555_ASSUMPTIONS;
  
  // P0.5 FIX: Validação para detectar uso incorreto de defaults
  if (beta === defaults.beta && process.env.NODE_ENV === 'development') {
    console.warn(
      `[P0.5 WARNING] Beta de ${empresa} é igual ao default (${beta}). ` +
      `Certifique-se de estar passando companyParams.${empresa}.beta e não o default!`
    );
  }
  
  // 🔧 WACC FIX: Calcular Ke via CAPM (empresas 100% equity: WACC = Ke)
  const keComponents = calculateKe({
    rf: macro.taxaSelic,
    rfMode: macro.rfMode || 'local',
    beta: beta,
    erp: macro.premioRiscoMercado,
    crp: macro.premioRiscoBrasil,
    crpMethodology: macro.crpMethodology || 'additive',
    sizePremium: macro.sizePremium || 0,
    specificRisk: macro.specificRisk || 0,
  });
  
  const assumptions: CompanyAssumptions = {
    ...defaults,
    // ✅ ANOS DINÂMICOS: baseYear é o último ano da DRE
    baseYear,
    // P0.5 FIX: TODOS os parâmetros editáveis pelo usuário
    beta,                           // ✅ M3: Beta editável
    capex_pct_receita: capex,       // ✅ M2: CAPEX editável
    nwc_pct_delta_receita: nwc,     // ✅ M2: NWC editável
    crescimento: scenario.crescimento,      // ✅ P0: Crescimento editável
    margemEBITDA: scenario.margemEBITDA,    // ✅ P0: Margem EBITDA editável
    wacc: keComponents.ke,          // 🔧 WACC FIX: WACC = Ke (100% equity)
  };
  
  // P0.5 FIX: Log para debug em desenvolvimento
  if (process.env.NODE_ENV === 'development') {
    console.log(`[createCompanyAssumptions] ${empresa}:`, {
      beta: assumptions.beta,
      wacc: assumptions.wacc,  // 🔧 WACC FIX: Mostrar WACC calculado
      capex_2025: assumptions.capex_pct_receita[2025],
      nwc_2025: assumptions.nwc_pct_delta_receita[2025],
      crescimento_2025: assumptions.crescimento[2025],
      margemEBITDA_2025: assumptions.margemEBITDA[2025],
    });
  }
  
  return assumptions;
}

// ============================================================================
// STATE INTERFACE
// ============================================================================

export interface ValuationState {
  /** Snapshot atual do valuation (Sétima + 555) */
  snapshot: ValuationSnapshot | null;
  
  /** ✅ ANOS DINÂMICOS: Último ano da DRE (base para projeções) */
  baseYear: number;
  
  /** Cenário atual selecionado */
  currentScenario: 'pessimista' | 'base' | 'otimista';
  
  /** Definições dos cenários disponíveis */
  scenarios: {
    setima: {
      pessimista: ScenarioDefinition;
      base: ScenarioDefinition;
      otimista: ScenarioDefinition;
    };
    studios555: {
      pessimista: ScenarioDefinition;
      base: ScenarioDefinition;
      otimista: ScenarioDefinition;
    };
  };
  
  /** M3: Parâmetros por empresa (não variam por cenário) */
  companyParams: {
    setima: {
      beta: number;
      capex_pct_receita: CompanyAssumptions['capex_pct_receita']; // M2
      nwc_pct_delta_receita: CompanyAssumptions['nwc_pct_delta_receita']; // M2
    };
    studios555: {
      beta: number;
      capex_pct_receita: CompanyAssumptions['capex_pct_receita']; // M2
      nwc_pct_delta_receita: CompanyAssumptions['nwc_pct_delta_receita']; // M2
    };
  };
  
  /** Premissas macroeconômicas (compartilhadas por ambas empresas) */
  macroAssumptions: MacroAssumptions;
  
  /** Equity Bridge */
  equityBridge: EquityBridgeInputs;
  
  /** Descontos */
  discounts: DiscountInputs;
  
  /** Participação */
  stake: {
    percentual: number;
    descricao?: string;
  };
  
  /** Flag: está carregando? */
  loading: boolean;
  
  /** Mensagem de erro (se houver) */
  error: string | null;
  
  /** Timestamp da última atualização */
  lastUpdated: string | null;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const DEFAULT_MACRO: MacroAssumptions = {
  taxaSelic: 0.1175,
  inflacao: 0.045,
  crescimentoPIB: 0.025,
  premioRiscoBrasil: 0.035,
  premioRiscoMercado: 0.08
};

const DEFAULT_EQUITY: EquityBridgeInputs = {
  dividaLiquida: 0,
  caixaExcedente: 0
};

const DEFAULT_DISCOUNTS: DiscountInputs = {
  kpd_mode: 'in_wacc',
  kpd_rate: 0.15,
  dloc: 0.10,
  dlom: 0.27
};

const DEFAULT_STAKE_CONFIG = {
  percentual: 1.0,
  descricao: 'Participação de 100% (integral)'
};

const initialState: ValuationState = {
  snapshot: null,
  baseYear: 2024, // ✅ ANOS DINÂMICOS: Padrão é 2024 (será atualizado ao carregar DRE)
  currentScenario: 'base',
  scenarios: {
    setima: {
      pessimista: createScenarioDefinition(
        SETIMA_CENARIO_PESSIMISTA,
        'Pessimista',
        'Crise de 2023 persiste; estrutura de custos não se ajusta',
        'Crescimento fraco (3-5%), margem estagnada (12-16%)',
        0.2
      ),
      base: createScenarioDefinition(
        SETIMA_CENARIO_BASE,
        'Base',
        'Recuperação gradual; volta aos patamares 2020-2021',
        'Crescimento moderado (8%), margem saudável (22%)',
        0.6
      ),
      otimista: createScenarioDefinition(
        SETIMA_CENARIO_OTIMISTA,
        'Otimista',
        'Recuperação acelerada; volta próximo ao pico 2022',
        'Crescimento acelerado (8-12%), margem alta (30%)',
        0.2
      )
    },
    studios555: {
      pessimista: createScenarioDefinition(
        STUDIOS555_CENARIO_PESSIMISTA,
        'Pessimista',
        'Desaceleração brusca; perda de competitividade',
        'Crescimento baixo (5-10%), margem em queda (27-30%)',
        0.2
      ),
      base: createScenarioDefinition(
        STUDIOS555_CENARIO_BASE,
        'Base',
        'Crescimento acelerado com desaceleração gradual',
        'Crescimento forte (15-20%), margem estável (35%)',
        0.6
      ),
      otimista: createScenarioDefinition(
        STUDIOS555_CENARIO_OTIMISTA,
        'Otimista',
        'Hipercrescimento sustentado; liderança de mercado',
        'Crescimento hiperbólico (12-30%), margem premium (38-41%)',
        0.2
      )
    }
  },
  companyParams: {
    setima: {
      beta: DEFAULT_SETIMA_ASSUMPTIONS.beta, // 0.95
      capex_pct_receita: DEFAULT_SETIMA_ASSUMPTIONS.capex_pct_receita, // M2
      nwc_pct_delta_receita: DEFAULT_SETIMA_ASSUMPTIONS.nwc_pct_delta_receita, // M2
    },
    studios555: {
      beta: DEFAULT_555_ASSUMPTIONS.beta, // 1.05
      capex_pct_receita: DEFAULT_555_ASSUMPTIONS.capex_pct_receita, // M2
      nwc_pct_delta_receita: DEFAULT_555_ASSUMPTIONS.nwc_pct_delta_receita, // M2
    },
  },
  macroAssumptions: DEFAULT_MACRO,
  equityBridge: DEFAULT_EQUITY,
  discounts: DEFAULT_DISCOUNTS,
  stake: DEFAULT_STAKE_CONFIG,
  loading: false,
  error: null,
  lastUpdated: null
};

// ============================================================================
// SLICE
// ============================================================================

const valuationSlice = createSlice({
  name: 'valuation',
  initialState,
  reducers: {
    /**
     * ✅ ANOS DINÂMICOS: Atualiza baseYear e recria premissas dinamicamente
     * 
     * Deve ser chamado quando:
     * - DRE é carregada inicialmente
     * - Usuário adiciona um novo ano na DRE
     * - Dados da DRE mudam
     * 
     * @param dreData - Dados completos da DRE ({ setima: {...}, studio555: {...} })
     */
    updateBaseYearFromDRE: (
      state,
      action: PayloadAction<{
        setima: { years: { year: number }[] };
        studio555: { years: { year: number }[] };
      } | null>
    ) => {
      const newBaseYear = detectBaseYear(action.payload);
      
      // Se baseYear não mudou, não fazer nada
      if (newBaseYear === state.baseYear) return;
      
      console.log(`[ValuationSlice] 🔄 BaseYear atualizado: ${state.baseYear} → ${newBaseYear}`);
      state.baseYear = newBaseYear;
      
      // Recriar premissas dinamicamente para o novo baseYear
      state.companyParams.setima.capex_pct_receita = generateDynamicCapex(
        newBaseYear,
        SETIMA_CAPEX_TEMPLATE
      );
      state.companyParams.setima.nwc_pct_delta_receita = generateDynamicNWC(
        newBaseYear,
        SETIMA_NWC_TEMPLATE
      );
      
      state.companyParams.studios555.capex_pct_receita = generateDynamicCapex(
        newBaseYear,
        STUDIOS555_CAPEX_TEMPLATE
      );
      state.companyParams.studios555.nwc_pct_delta_receita = generateDynamicNWC(
        newBaseYear,
        STUDIOS555_NWC_TEMPLATE
      );
      
      // Atualizar cenários com anos dinâmicos
      ['pessimista' as const, 'base' as const, 'otimista' as const].forEach((cenario) => {
        // Sétima - usar crescimento e margens específicas do cenário
        const setimaScenario = cenario === 'pessimista' 
          ? SETIMA_CENARIO_PESSIMISTA
          : cenario === 'otimista'
          ? SETIMA_CENARIO_OTIMISTA
          : SETIMA_CENARIO_BASE;
        
        // ✅ FIX: Manter valores diferenciados por cenário
        // Converter anos fixos (2025-2029) para anos dinâmicos (baseYear+1 a baseYear+5)
        const firstYear = newBaseYear + 1;
        const crescimentoAnos = Object.entries(setimaScenario.crescimento)
          .filter(([key]) => key !== 'perpetuo')
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([_, value]) => value);
        
        state.scenarios.setima[cenario].crescimento = {
          [firstYear]: crescimentoAnos[0],
          [firstYear + 1]: crescimentoAnos[1],
          [firstYear + 2]: crescimentoAnos[2],
          [firstYear + 3]: crescimentoAnos[3],
          [firstYear + 4]: crescimentoAnos[4],
          perpetuo: setimaScenario.crescimento.perpetuo
        };
        
        const margemAnos = Object.entries(setimaScenario.margemEBITDA)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([_, value]) => value);
        
        state.scenarios.setima[cenario].margemEBITDA = {
          [firstYear]: margemAnos[0],
          [firstYear + 1]: margemAnos[1],
          [firstYear + 2]: margemAnos[2],
          [firstYear + 3]: margemAnos[3],
          [firstYear + 4]: margemAnos[4],
        };
        
        // 555 Studios - usar crescimento e margens específicas do cenário
        const studios555Scenario = cenario === 'pessimista'
          ? STUDIOS555_CENARIO_PESSIMISTA
          : cenario === 'otimista'
          ? STUDIOS555_CENARIO_OTIMISTA
          : STUDIOS555_CENARIO_BASE;
        
        const crescimento555Anos = Object.entries(studios555Scenario.crescimento)
          .filter(([key]) => key !== 'perpetuo')
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([_, value]) => value);
        
        state.scenarios.studios555[cenario].crescimento = {
          [firstYear]: crescimento555Anos[0],
          [firstYear + 1]: crescimento555Anos[1],
          [firstYear + 2]: crescimento555Anos[2],
          [firstYear + 3]: crescimento555Anos[3],
          [firstYear + 4]: crescimento555Anos[4],
          perpetuo: studios555Scenario.crescimento.perpetuo
        };
        
        const margem555Anos = Object.entries(studios555Scenario.margemEBITDA)
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([_, value]) => value);
        
        state.scenarios.studios555[cenario].margemEBITDA = {
          [firstYear]: margem555Anos[0],
          [firstYear + 1]: margem555Anos[1],
          [firstYear + 2]: margem555Anos[2],
          [firstYear + 3]: margem555Anos[3],
          [firstYear + 4]: margem555Anos[4],
        };
      });
      
      // Recalcular valuation com novo baseYear
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation com novo baseYear';
      }
    },
    
    /**
     * Define o snapshot completo do valuation
     */
    setSnapshot: (state, action: PayloadAction<ValuationSnapshot>) => {
      state.snapshot = action.payload;
      state.lastUpdated = new Date().toISOString();
      state.error = null;
    },
    
    /**
     * Atualiza o cenário atual e recalcula valuation
     */
    updateScenario: (state, action: PayloadAction<'pessimista' | 'base' | 'otimista'>) => {
      state.currentScenario = action.payload;
      state.loading = true;
      
      try {
        // Criar assumptions completos a partir do cenário
        const setimaScenario = state.scenarios.setima[action.payload];
        const studios555Scenario = state.scenarios.studios555[action.payload];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
      }
    },
    
    /**
     * Atualiza premissas de uma empresa específica em um cenário
     * 
     * @M3 - Expandido para aceitar beta editável
     */
    updateCompanyScenario: (
      state, 
      action: PayloadAction<{
        empresa: 'setima' | 'studios555';
        cenario: 'pessimista' | 'base' | 'otimista';
        updates: Partial<Pick<ScenarioDefinition, 'crescimento' | 'margemBruta' | 'margemEBITDA' | 'margemLiquida'>>;
      }>
    ) => {
      const { empresa, cenario, updates } = action.payload;
      
      // Atualizar cenário específico
      const currentScenario = state.scenarios[empresa][cenario];
      state.scenarios[empresa][cenario] = {
        ...currentScenario,
        crescimento: updates.crescimento || currentScenario.crescimento,
        margemBruta: updates.margemBruta || currentScenario.margemBruta,
        margemEBITDA: updates.margemEBITDA || currentScenario.margemEBITDA,
        margemLiquida: updates.margemLiquida || currentScenario.margemLiquida
      };
      
      // Se for o cenário atual, recalcular
      if (cenario === state.currentScenario) {
        state.loading = true;
        
        try {
          const setimaScenario = state.scenarios.setima[cenario];
          const studios555Scenario = state.scenarios.studios555[cenario];
          
          const setimaAssumptions = createCompanyAssumptions(
            'setima',
            setimaScenario,
            state.companyParams.setima.beta,
            state.companyParams.setima.capex_pct_receita,
            state.companyParams.setima.nwc_pct_delta_receita,
            state.macroAssumptions,  // 🔧 WACC FIX
            state.baseYear  // ✅ ANOS DINÂMICOS
          );
          
          const studios555Assumptions = createCompanyAssumptions(
            'studios555',
            studios555Scenario,
            state.companyParams.studios555.beta,
            state.companyParams.studios555.capex_pct_receita,
            state.companyParams.studios555.nwc_pct_delta_receita,
            state.macroAssumptions,  // 🔧 WACC FIX
            state.baseYear  // ✅ ANOS DINÂMICOS
          );
          
          const engine = new ValuationEngine();
          const newSnapshot = engine.calculate({
            macro: state.macroAssumptions,
            setima: setimaAssumptions,
            studios555: studios555Assumptions,
            equityBridge: state.equityBridge,
            discounts: state.discounts,
            stake: state.stake
          });
          
          state.snapshot = newSnapshot;
          state.lastUpdated = new Date().toISOString();
          state.loading = false;
          state.error = null;
        } catch (error) {
          state.loading = false;
          state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
        }
      }
    },
    
    /**
     * Atualiza premissas macroeconômicas e recalcula tudo
     */
    updateMacroAssumptions: (state, action: PayloadAction<Partial<MacroAssumptions>>) => {
      state.macroAssumptions = {
        ...state.macroAssumptions,
        ...action.payload
      };
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
      }
    },
    
    /**
     * M3: Atualiza beta de uma empresa específica
     * 
     * Beta é um parâmetro constante da empresa (não varia por cenário)
     */
    updateCompanyBeta: (
      state,
      action: PayloadAction<{
        empresa: 'setima' | 'studios555';
        beta: number;
      }>
    ) => {
      const { empresa, beta } = action.payload;
      
      // Atualizar beta no companyParams
      state.companyParams[empresa].beta = beta;
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake,
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation com novo beta';
      }
    },
    
    /**
     * M2: Atualiza CAPEX e NWC de uma empresa específica
     * 
     * CAPEX e NWC são parâmetros constantes da empresa (não variam por cenário)
     */
    updateCompanyCapexNWC: (
      state,
      action: PayloadAction<{
        empresa: 'setima' | 'studios555';
        capex?: CompanyAssumptions['capex_pct_receita'];
        nwc?: CompanyAssumptions['nwc_pct_delta_receita'];
      }>
    ) => {
      const { empresa, capex, nwc } = action.payload;
      
      // Atualizar CAPEX e/ou NWC no companyParams
      if (capex) {
        state.companyParams[empresa].capex_pct_receita = capex;
      }
      if (nwc) {
        state.companyParams[empresa].nwc_pct_delta_receita = nwc;
      }
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation com novo CAPEX/NWC';
      }
    },
    
    /**
     * Atualiza Equity Bridge
     */
    updateEquityBridge: (state, action: PayloadAction<EquityBridgeInputs>) => {
      state.equityBridge = {
        ...state.equityBridge,
        ...action.payload
      };
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );

        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
      }
    },
    
    /**
     * Atualiza Descontos e recalcula
     */
    updateDiscounts: (state, action: PayloadAction<Partial<DiscountInputs>>) => {
      state.discounts = {
        ...state.discounts,
        ...action.payload
      };
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
      }
    },
    
    /**
     * Atualiza participação (stake) e recalcula
     */
    updateStake: (state, action: PayloadAction<{ percentual?: number; descricao?: string }>) => {
      state.stake = {
        ...state.stake,
        ...action.payload
      };
      
      state.loading = true;
      
      try {
        const setimaScenario = state.scenarios.setima[state.currentScenario];
        const studios555Scenario = state.scenarios.studios555[state.currentScenario];
        
        const setimaAssumptions = createCompanyAssumptions(
          'setima',
          setimaScenario,
          state.companyParams.setima.beta,
          state.companyParams.setima.capex_pct_receita,
          state.companyParams.setima.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const studios555Assumptions = createCompanyAssumptions(
          'studios555',
          studios555Scenario,
          state.companyParams.studios555.beta,
          state.companyParams.studios555.capex_pct_receita,
          state.companyParams.studios555.nwc_pct_delta_receita,
          state.macroAssumptions,  // 🔧 WACC FIX
          state.baseYear  // ✅ ANOS DINÂMICOS
        );
        
        const engine = new ValuationEngine();

        const newSnapshot = engine.calculate({
          macro: state.macroAssumptions,
          setima: setimaAssumptions,
          studios555: studios555Assumptions,
          equityBridge: state.equityBridge,
          discounts: state.discounts,
          stake: state.stake
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao recalcular valuation';
      }
    },
    
    /**
     * Reseta para valores padrão (cenário base)
     */
    resetToDefaults: (state) => {
      state.currentScenario = 'base';
      state.macroAssumptions = DEFAULT_MACRO;
      state.equityBridge = DEFAULT_EQUITY;
      state.discounts = DEFAULT_DISCOUNTS;
      state.stake = DEFAULT_STAKE_CONFIG;
      
      // Recalcular com valores padrão
      try {
        const engine = new ValuationEngine();
        const newSnapshot = engine.calculate({
          macro: DEFAULT_MACRO,
          setima: DEFAULT_SETIMA_ASSUMPTIONS,
          studios555: DEFAULT_555_ASSUMPTIONS,
          equityBridge: DEFAULT_EQUITY,
          discounts: DEFAULT_DISCOUNTS,
          stake: DEFAULT_STAKE_CONFIG
        });
        
        state.snapshot = newSnapshot;
        state.lastUpdated = new Date().toISOString();
        state.loading = false;
        state.error = null;
      } catch (error) {
        state.loading = false;
        state.error = error instanceof Error ? error.message : 'Erro ao resetar valuation';
      }
    },
    
    /**
     * Define estado de loading manualmente
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    /**
     * Define erro manualmente
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  }
});

// ============================================================================
// ACTIONS
// ============================================================================

export const {
  updateBaseYearFromDRE, // ✅ ANOS DINÂMICOS: Nova action para atualizar baseYear da DRE
  setSnapshot,
  updateScenario,
  updateCompanyScenario,
  updateMacroAssumptions,
  updateCompanyBeta, // ✅ M3: Nova action para betas editáveis
  updateCompanyCapexNWC, // ✅ M2: Nova action para CAPEX/NWC editáveis
  updateEquityBridge,
  updateDiscounts,
  updateStake,
  resetToDefaults,
  setLoading,
  setError
} = valuationSlice.actions;

// ============================================================================
// REDUCER
// ============================================================================

export default valuationSlice.reducer;
